import { useEffect, useState, useRef } from "react";
import { AppShell } from "../components/AppShell";
import { PasswordSecurity } from "../components/PasswordSecurity";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/apiClient";
import { toast } from "sonner";
import { Check, Loader2, Crown, Sparkles, Zap, Download, Puzzle, Radio, PowerOff, Plus, Trash2, RefreshCw, Eye, EyeOff, UserRound, MapPin, Smartphone, Film, Cpu } from "lucide-react";

const PLAN_ICON = { free: Zap, pro: Sparkles, ultra: Crown };

function UsageBar({ label, used, quota }) {
  const unlimited = quota === -1;
  const pct = unlimited ? 8 : Math.min(100, Math.round((used / Math.max(1, quota)) * 100));
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="font-mono-hud text-[10px] tracking-[0.15em] text-[#8b949e] uppercase">{label}</span>
        <span className="font-mono-hud text-[11px] text-white tabular-nums">
          {used.toLocaleString("fr-FR")} {unlimited ? "/ ∞" : `/ ${quota.toLocaleString("fr-FR")}`}
        </span>
      </div>
      <div className="h-2 clip-corner bg-[rgba(2,8,18,0.8)] border border-[var(--border-hud)] overflow-hidden">
        <div className="h-full transition-all" style={{ width: `${pct}%`, background: "var(--accent)", boxShadow: "var(--accent-glow)" }} />
      </div>
    </div>
  );
}

export default function Account() {
  const { user, setUser, updateSettings } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [assistantName, setAssistantName] = useState(user?.assistant_name || "Sara Connor");
  const [savingProfile, setSavingProfile] = useState(false);

  const [sub, setSub] = useState(null);
  const [plans, setPlans] = useState([]);
  const [switching, setSwitching] = useState(null);

  const [agentStatus, setAgentStatus] = useState(null);
  const [revoking, setRevoking] = useState(false);

  // D-ID Manager (avatar temps réel — rotation jusqu'à 10 clés)
  const [didKeys, setDidKeys] = useState([]);
  const [didActive, setDidActive] = useState(null);
  const [didMax, setDidMax] = useState(10);
  const [didLog, setDidLog] = useState([]);
  const [didAddOpen, setDidAddOpen] = useState(false);
  const [didNewName, setDidNewName] = useState("");
  const [didNewKey, setDidNewKey] = useState("");
  const [didReveal, setDidReveal] = useState({});
  const [didBusy, setDidBusy] = useState(null);
  const [didSource, setDidSource] = useState(null);
  const [didUploading, setDidUploading] = useState(false);
  const didFileRef = useRef(null);

  // Agnes AI Manager (moteur IA — texte/code/vision — rotation jusqu'à 10 clés)
  const [agKeys, setAgKeys] = useState([]);
  const [agActive, setAgActive] = useState(null);
  const [agMax, setAgMax] = useState(10);
  const [agModel, setAgModel] = useState("agnes-2.5-flash");
  const [agLog, setAgLog] = useState([]);
  const [agAddOpen, setAgAddOpen] = useState(false);
  const [agNewName, setAgNewName] = useState("");
  const [agNewKey, setAgNewKey] = useState("");
  const [agReveal, setAgReveal] = useState({});
  const [agBusy, setAgBusy] = useState(null);
  const [agAlert, setAgAlert] = useState(false);
  const [agTotals, setAgTotals] = useState({ tokens: 0, images: 0 });

  // Profile fields (for Auto-Fill)
  const [profile, setProfile] = useState({
    full_name: "", phone: "", address_line1: "", address_line2: "",
    city: "", zip_code: "", country: "",
  });
  const [savingProf, setSavingProf] = useState(false);

  const loadDid = () => {
    api.get("/user/did-keys").then(({ data }) => {
      setDidKeys(data.keys || []);
      setDidActive(data.active_id);
      setDidMax(data.max || 10);
    }).catch(() => {});
    api.get("/user/did-activity?limit=10").then(({ data }) => setDidLog(data.entries || [])).catch(() => {});
    api.get("/did/config").then(({ data }) => setDidSource(data)).catch(() => {});
  };

  const loadAgnes = () => {
    api.get("/user/agnes-keys").then(({ data }) => {
      setAgKeys(data.keys || []);
      setAgActive(data.active_id);
      setAgMax(data.max || 10);
      setAgModel(data.chat_model || "agnes-2.5-flash");
      setAgAlert(!!data.alert);
      setAgTotals({ tokens: data.total_tokens || 0, images: data.total_images || 0 });
    }).catch(() => {});
    api.get("/user/agnes-activity?limit=10").then(({ data }) => setAgLog(data.entries || [])).catch(() => {});
  };

  useEffect(() => {
    api.get("/billing/subscription").then(({ data }) => setSub(data)).catch(() => {});
    api.get("/billing/plans").then(({ data }) => setPlans(data.plans || [])).catch(() => {});
    api.get("/user/profile-fields").then(({ data }) => setProfile((p) => ({ ...p, ...(data.profile || {}) }))).catch(() => {});
    loadDid();
    loadAgnes();
    const loadAgent = () => api.get("/agent/status").then(({ data }) => setAgentStatus(data)).catch(() => {});
    loadAgent();
    const iv = setInterval(loadAgent, 4000);
    return () => clearInterval(iv);
  }, []); // eslint-disable-line

  const addDidKey = async (e) => {
    e.preventDefault();
    if (!didNewName || !didNewKey) return;
    setDidBusy("add");
    try {
      await api.post("/user/did-keys", { name: didNewName.trim(), api_key: didNewKey.trim() });
      setDidNewName(""); setDidNewKey(""); setDidAddOpen(false);
      toast.success("Clé D-ID ajoutée");
      loadDid();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Ajout impossible");
    } finally { setDidBusy(null); }
  };

  const activateDidKey = async (id) => {
    try {
      await api.patch(`/user/did-keys/${id}`, { active: true });
      toast.success("Clé activée");
      loadDid();
    } catch { toast.error("Échec"); }
  };

  const testDidKey = async (id) => {
    setDidBusy(id);
    try {
      const { data } = await api.post(`/user/did-keys/${id}/test`);
      toast.success(
        data.status === "active" ? (data.credits_left != null ? `Clé OK — ${data.credits_left} crédits restants` : "Clé OK") :
        data.status === "quota_exceeded" ? "Crédits épuisés — Sara basculera sur la suivante" :
        data.status === "invalid" ? (data.error || "Clé invalide") : "Statut inconnu"
      );
      loadDid();
    } catch (err) { toast.error(err?.response?.data?.detail || "Test échoué"); }
    finally { setDidBusy(null); }
  };

  const deleteDidKey = async (id) => {
    if (!confirm("Supprimer cette clé D-ID ?")) return;
    try {
      await api.delete(`/user/did-keys/${id}`);
      toast.success("Clé supprimée");
      loadDid();
    } catch { toast.error("Échec"); }
  };

  const addAgnesKey = async (e) => {
    e.preventDefault();
    if (!agNewName || !agNewKey) return;
    setAgBusy("add");
    try {
      await api.post("/user/agnes-keys", { name: agNewName.trim(), api_key: agNewKey.trim() });
      setAgNewName(""); setAgNewKey(""); setAgAddOpen(false);
      toast.success("Clé Agnes ajoutée");
      loadAgnes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Ajout impossible");
    } finally { setAgBusy(null); }
  };

  const activateAgnesKey = async (id) => {
    try {
      await api.patch(`/user/agnes-keys/${id}`, { active: true });
      toast.success("Clé activée");
      loadAgnes();
    } catch { toast.error("Échec"); }
  };

  const testAgnesKey = async (id) => {
    setAgBusy(id);
    try {
      const { data } = await api.post(`/user/agnes-keys/${id}/test`);
      toast.success(
        data.status === "active" ? "Clé Agnes OK" :
        data.status === "quota_exceeded" ? "Crédits épuisés — Sara basculera sur la suivante" :
        data.status === "rate_limited" ? "Limite de débit atteinte — réessaie" :
        data.status === "invalid" ? (data.error || "Clé invalide") : "Statut inconnu"
      );
      loadAgnes();
    } catch (err) { toast.error(err?.response?.data?.detail || "Test échoué"); }
    finally { setAgBusy(null); }
  };

  const deleteAgnesKey = async (id) => {
    if (!confirm("Supprimer cette clé Agnes ?")) return;
    try {
      await api.delete(`/user/agnes-keys/${id}`);
      toast.success("Clé supprimée");
      loadAgnes();
    } catch { toast.error("Échec"); }
  };

  const uploadFace = async (e) => {
    const file = e.target.files?.[0];
    if (didFileRef.current) didFileRef.current.value = "";
    if (!file) return;
    setDidUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/did/source/upload", form);
      setDidSource((prev) => ({ ...(prev || {}), source_url: data.source_url, is_custom: true }));
      toast.success("Visage de Sara mis à jour");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'envoi de l'image");
    } finally {
      setDidUploading(false);
    }
  };

  const resetFace = async () => {
    try {
      const { data } = await api.post("/did/source", { reset: true });
      const cfg = await api.get("/did/config");
      setDidSource(cfg.data);
      toast.success("Visage réinitialisé (Sara par défaut)");
    } catch { toast.error("Échec"); }
  };

  const saveProfile2 = async () => {
    setSavingProf(true);
    try {
      await api.post("/user/profile-fields", profile);
      toast.success("Profil enregistré");
    } catch { toast.error("Enregistrement échoué"); }
    finally { setSavingProf(false); }
  };

  const revokeAgent = async () => {
    if (!confirm("Déconnecter l'extension Sara Copilote de tous les appareils ?")) return;
    setRevoking(true);
    try {
      await api.post("/agent/revoke");
      toast.success("Extension déconnectée");
      const { data } = await api.get("/agent/status");
      setAgentStatus(data);
    } catch (e) {
      toast.error("Échec de la révocation");
    } finally {
      setRevoking(false);
    }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await updateSettings({ name, assistant_name: assistantName });
      toast.success("Profil enregistré");
    } catch (e) {
      toast.error("Échec de l'enregistrement");
    } finally {
      setSavingProfile(false);
    }
  };

  const choosePlan = async (planId) => {
    setSwitching(planId);
    try {
      const plan = plans.find((p) => p.id === planId);
      if (plan?.lookup_key) {
        // paid plan → Stripe Checkout (subscription)
        const { data } = await api.post("/payments/checkout", {
          lookup_key: plan.lookup_key, origin_url: window.location.origin,
        });
        window.location.href = data.checkout_url;
        return;
      }
      // free plan → downgrade
      const { data } = await api.post("/billing/subscribe", { plan: planId });
      setUser(data);
      const { data: s } = await api.get("/billing/subscription");
      setSub(s);
      toast.success(`Offre « ${data.plan} » activée`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de changer d'offre");
    } finally {
      setSwitching(null);
    }
  };

  const startTrial = async () => {
    setSwitching("trial");
    try {
      const { data } = await api.post("/billing/start-trial");
      setUser(data);
      const { data: s } = await api.get("/billing/subscription");
      setSub(s);
      toast.success("Essai gratuit de 7 jours activé — profite de Pro !");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Essai indisponible");
    } finally {
      setSwitching(null);
    }
  };

  const openPortal = async () => {
    setSwitching("portal");
    try {
      const { data } = await api.post("/billing/portal", { return_url: window.location.href });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Portail indisponible");
      setSwitching(null);
    }
  };

  const currentPlanId = sub?.plan?.id || user?.plan || "free";
  const quota = sub?.plan || { quota_messages: 100, quota_documents: 5, quota_voice_chars: 15000 };
  const usage = sub?.usage || { messages: 0, documents: 0, voice_chars: 0 };

  return (
    <AppShell title="Mon compte" subtitle="Profil, abonnement et voix de Sara">
      <div className="grid lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Profile */}
        <section className="hud-panel reticle clip-corner p-5 sm:p-6">
          <span className="rc-tr" />
          <span className="rc-bl" />
          <h3 className="font-mono-hud text-[10px] tracking-[0.25em] text-[#8b949e] uppercase mb-4">Profil opérateur</h3>
          <div className="space-y-4">
            <div>
              <label className="font-mono-hud text-[10px] tracking-[0.2em] text-[#8b949e] block mb-1.5">E-MAIL</label>
              <input value={user?.email || ""} disabled className="hud-input opacity-60 cursor-not-allowed" data-testid="account-email" />
            </div>
            <div>
              <label className="font-mono-hud text-[10px] tracking-[0.2em] text-[#8b949e] block mb-1.5">VOTRE NOM</label>
              <input value={name} onChange={(e) => setName(e.target.value)} className="hud-input" data-testid="account-name-input" />
            </div>
            <div>
              <label className="font-mono-hud text-[10px] tracking-[0.2em] text-[#8b949e] block mb-1.5">NOM DE L'ASSISTANTE</label>
              <input value={assistantName} onChange={(e) => setAssistantName(e.target.value)} className="hud-input" data-testid="account-assistant-input" />
            </div>
            <button
              data-testid="account-save-profile"
              onClick={saveProfile}
              disabled={savingProfile}
              className="hud-btn clip-corner w-full py-3 text-sm font-bold disabled:opacity-50"
            >
              {savingProfile ? "ENREGISTREMENT..." : "ENREGISTRER LE PROFIL"}
            </button>
          </div>
        </section>

        {/* Usage */}
        <section className="hud-panel clip-corner p-5 sm:p-6">
          <h3 className="font-mono-hud text-[10px] tracking-[0.25em] text-[#8b949e] uppercase mb-4">
            Consommation du mois — offre {sub?.plan?.name || "…"}
          </h3>
          <div className="space-y-4">
            <UsageBar label="Messages" used={usage.messages} quota={quota.quota_messages} />
            <UsageBar label="Documents indexés" used={usage.documents} quota={quota.quota_documents} />
            <UsageBar label="Caractères vocalisés" used={usage.voice_chars} quota={quota.quota_voice_chars} />
          </div>
        </section>
      </div>

      <section className="mt-6">
        <PasswordSecurity testid="account-security-panel" />
      </section>

      {/* Plans */}
      <section className="mt-6">
        <h3 className="font-mono-hud text-[10px] tracking-[0.25em] text-[#8b949e] uppercase mb-1">Offres d'abonnement</h3>
        <p className="font-mono-hud text-[10px] text-[#6b7a8d] mb-4">
          Paiement sécurisé par Stripe. Carte de test : 4242 4242 4242 4242, date future, CVC au choix.
        </p>
        {user?.subscription_status === "active" && currentPlanId !== "free" && (
          <div className="mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 clip-corner border border-[var(--border-hud)] bg-[rgba(2,8,18,0.6)] p-3.5" data-testid="manage-subscription-block">
            <div className="text-sm text-[#dbe7f3]">
              Offre active : <span className="accent-text font-bold">{sub?.plan?.name || currentPlanId}</span>. Gère ta carte, tes factures ou résilie à tout moment.
            </div>
            <button
              data-testid="manage-subscription-button"
              onClick={openPortal}
              disabled={switching === "portal"}
              className="hud-btn clip-corner px-4 py-2 text-[11px] font-bold whitespace-nowrap disabled:opacity-50"
            >
              {switching === "portal" ? <Loader2 className="h-4 w-4 animate-spin" /> : "GÉRER MON ABONNEMENT"}
            </button>
          </div>
        )}
        {!user?.trial_used && currentPlanId === "free" && (
          <div data-testid="trial-banner" className="mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 clip-corner border border-[var(--accent)] bg-[var(--accent-a10)] p-3.5">
            <div className="text-sm text-[#dbe7f3]">
              <span className="accent-text font-bold">Essai gratuit 7 jours</span> — débloque toutes les fonctions Pro, sans carte bancaire.
            </div>
            <button
              data-testid="start-trial-button"
              onClick={startTrial}
              disabled={switching === "trial"}
              className="hud-btn clip-corner px-4 py-2 text-[11px] font-bold whitespace-nowrap disabled:opacity-50"
            >
              {switching === "trial" ? <Loader2 className="h-4 w-4 animate-spin" /> : "DÉMARRER L'ESSAI"}
            </button>
          </div>
        )}
        <div className="grid sm:grid-cols-3 gap-3 sm:gap-4" data-testid="plans-grid">
          {plans.map((p) => {
            const Icon = PLAN_ICON[p.id] || Zap;
            const active = p.id === currentPlanId;
            return (
              <div
                key={p.id}
                data-testid={`plan-card-${p.id}`}
                className={`hud-panel clip-corner p-5 flex flex-col ${active ? "border-[var(--accent)] shadow-[var(--accent-glow)]" : ""}`}
              >
                <div className="flex items-center gap-2">
                  <Icon className="h-5 w-5 accent-text" />
                  <span className="font-display text-lg font-black tracking-wide text-white">{p.name}</span>
                </div>
                <div className="mt-3 font-display text-2xl font-black accent-text">
                  {p.price_eur === 0 ? "Gratuit" : `${p.price_eur} €`}
                  {p.price_eur !== 0 && <span className="text-xs text-[#6b7a8d] font-mono-hud"> /mois</span>}
                </div>
                <ul className="mt-4 space-y-2 flex-1">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-[#9fb2c6]">
                      <Check className="h-3.5 w-3.5 accent-text shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`plan-choose-${p.id}`}
                  onClick={() => !active && choosePlan(p.id)}
                  disabled={active || switching === p.id}
                  className={`mt-5 clip-corner w-full py-2.5 text-[11px] font-bold flex items-center justify-center gap-2 transition-all ${
                    active
                      ? "border border-[var(--accent)] text-[var(--accent)] font-mono-hud tracking-[0.15em] uppercase cursor-default"
                      : "hud-btn"
                  }`}
                >
                  {switching === p.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : active ? (
                    "OFFRE ACTUELLE"
                  ) : p.lookup_key ? (
                    "S'ABONNER"
                  ) : (
                    "CHOISIR CETTE OFFRE"
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {/* Agnes AI Manager — moteur IA (texte/code/vision) multi-key rotation */}
      <section className="mt-6 hud-panel reticle clip-corner p-5 sm:p-6" data-testid="agnes-manager">
        <span className="rc-tr" />
        <span className="rc-bl" />
        <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <Cpu className="h-4 w-4 accent-text" />
              <h3 className="font-mono-hud text-[10px] tracking-[0.25em] text-[#8b949e] uppercase">
                Agnes AI · Moteur IA · Multi-Clés
              </h3>
            </div>
            <p className="mt-2 text-sm text-[#9fb2c6] max-w-xl leading-relaxed">
              Le cerveau de Sara : <strong className="accent-text">texte, code et vision</strong> via Agnes AI
              (<span className="font-mono-hud accent-text">{agModel}</span>). Jusqu'à <strong className="accent-text">{agMax} clés</strong> —
              quand la clé active tombe en panne de crédits, Sara bascule automatiquement sur la suivante.
            </p>
          </div>
          <button
            data-testid="agnes-add-btn"
            onClick={() => setAgAddOpen(!agAddOpen)}
            disabled={agKeys.length >= agMax}
            className="hud-btn clip-corner px-4 py-2 text-[11px] flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Plus className="h-4 w-4" />
            {agAddOpen ? "ANNULER" : `AJOUTER (${agKeys.length}/${agMax})`}
          </button>
        </div>

        {agKeys.length > 0 && (
          <div className="mb-4 flex flex-wrap items-center gap-3" data-testid="agnes-usage-summary">
            <span className="font-mono-hud text-[10px] tracking-[0.15em] uppercase text-[#6b7a8d]">Consommation :</span>
            <span className="font-mono-hud text-[11px] text-[#c8d6e5] tabular-nums">
              {agTotals.tokens.toLocaleString("fr-FR")} tokens · {agTotals.images} image{agTotals.images > 1 ? "s" : ""}
            </span>
            <span className="text-[10px] text-[#556] italic">(Agnes ne fournit pas de solde ; suivi local par clé)</span>
          </div>
        )}
        {agAlert && (
          <div
            className="mb-4 flex items-center gap-2 clip-corner border px-3 py-2 font-mono-hud text-[11px] tracking-wide"
            style={{ borderColor: "rgba(245,158,11,0.5)", background: "rgba(245,158,11,0.08)", color: "#f4c877" }}
            data-testid="agnes-quota-alert"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Une clé Agnes est en quota/limite — ajoute ou active une autre clé pour éviter toute coupure.
          </div>
        )}

        {agAddOpen && (
          <form onSubmit={addAgnesKey} className="mt-2 mb-4 grid sm:grid-cols-[1fr_2fr_auto] gap-2.5 items-end" data-testid="agnes-add-form">
            <div>
              <label className="font-mono-hud text-[10px] tracking-[0.2em] text-[#8b949e] block mb-1.5">NOM</label>
              <input
                data-testid="agnes-input-name"
                required
                value={agNewName}
                onChange={(e) => setAgNewName(e.target.value)}
                placeholder="Agnes 1"
                className="hud-input"
              />
            </div>
            <div>
              <label className="font-mono-hud text-[10px] tracking-[0.2em] text-[#8b949e] block mb-1.5">CLÉ API AGNES</label>
              <input
                data-testid="agnes-input-key"
                required
                type="password"
                value={agNewKey}
                onChange={(e) => setAgNewKey(e.target.value)}
                placeholder="sk-..."
                className="hud-input font-mono-hud"
              />
              <p className="mt-1.5 text-[10px] leading-relaxed text-[#6b7a8d]" data-testid="agnes-key-hint">
                Génère ta clé sur <span className="accent-text">platform.agnes-ai.com → API Keys</span> (format <span className="accent-text font-mono-hud">sk-…</span>). La clé est validée puis stockée de façon sécurisée.
              </p>
            </div>
            <button
              data-testid="agnes-submit"
              type="submit"
              disabled={agBusy === "add"}
              className="hud-btn clip-corner px-4 py-2.5 text-[11px] font-bold flex items-center gap-2 whitespace-nowrap"
            >
              {agBusy === "add" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              ENREGISTRER
            </button>
          </form>
        )}

        {agKeys.length === 0 ? (
          <div className="mt-2 clip-corner border border-dashed border-[var(--border-hud)] p-6 text-center text-[#6b7a8d] font-mono-hud text-[10px] tracking-[0.2em] uppercase" data-testid="agnes-empty">
            Aucune clé Agnes · Sara utilise le moteur de secours tant qu'aucune clé n'est ajoutée.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="agnes-list">
            {agKeys.map((k) => {
              const revealed = agReveal[k.id];
              const isActive = k.id === agActive;
              const statusColor =
                k.status === "active" ? "#00ffaa" :
                k.status === "quota_exceeded" ? "#f59e0b" :
                k.status === "rate_limited" ? "#f59e0b" :
                k.status === "invalid" ? "#ff5470" : "#8b949e";
              return (
                <li
                  key={k.id}
                  data-testid={`agnes-row-${k.id}`}
                  className="clip-corner border p-3.5 flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4 bg-[rgba(2,8,18,0.4)]"
                  style={{ borderColor: isActive ? "var(--accent)" : "var(--border-hud)" }}
                >
                  <div className="flex items-start gap-2 min-w-0 w-full sm:flex-1">
                    <span className="h-2 w-2 rounded-full shrink-0 mt-1.5" style={{ background: statusColor, boxShadow: `0 0 8px ${statusColor}` }} />
                    <div className="min-w-0">
                      <div className="text-white font-medium truncate flex items-center gap-2">
                        {k.name}
                        {isActive && (
                          <span className="font-mono-hud text-[9px] tracking-[0.2em] uppercase accent-text border border-[var(--accent-a25)] px-1.5 py-0.5 clip-corner">
                            EN COURS
                          </span>
                        )}
                      </div>
                      <div className="font-mono-hud text-[10px] text-[#8b949e] tracking-[0.1em] flex items-center gap-2">
                        <span className="truncate">{revealed ? k.api_key_masked.replace("...", "•••") : k.api_key_masked}</span>
                        <button
                          onClick={() => setAgReveal({ ...agReveal, [k.id]: !revealed })}
                          className="text-[#6b7a8d] hover:text-white shrink-0"
                          title={revealed ? "Masquer" : "Voir"}
                        >
                          {revealed ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                        </button>
                      </div>
                      <div className="mt-0.5 font-mono-hud text-[10px] uppercase tracking-[0.15em]" style={{ color: statusColor }}>
                        {k.status}
                      </div>
                      <div className="mt-0.5 font-mono-hud text-[10px] text-[#6b7a8d] tracking-[0.1em]" data-testid={`agnes-usage-${k.id}`}>
                        {(k.usage_requests || 0)} req · {(k.usage_tokens || 0).toLocaleString("fr-FR")} tokens · {(k.usage_images || 0)} img
                      </div>
                      {k.last_error && k.status === "invalid" && (
                        <div className="mt-1 text-[10px] leading-relaxed text-[#f0a6b2]">{k.last_error}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1.5 flex-wrap shrink-0">
                    {!isActive && k.status !== "invalid" && (
                      <button
                        data-testid={`agnes-activate-${k.id}`}
                        onClick={() => activateAgnesKey(k.id)}
                        className="clip-corner px-2.5 py-1.5 text-[10px] font-mono-hud tracking-[0.15em] uppercase border border-[var(--accent-a25)] accent-text hover:bg-[var(--accent-a10)]"
                        title="Rendre active"
                      >
                        Activer
                      </button>
                    )}
                    <button
                      data-testid={`agnes-test-${k.id}`}
                      onClick={() => testAgnesKey(k.id)}
                      disabled={agBusy === k.id}
                      className="clip-corner p-1.5 border border-[var(--border-hud)] text-[#8b949e] hover:text-white hover:bg-[var(--accent-a10)] disabled:opacity-40"
                      title="Vérifier la clé"
                    >
                      {agBusy === k.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                    </button>
                    <button
                      data-testid={`agnes-delete-${k.id}`}
                      onClick={() => deleteAgnesKey(k.id)}
                      className="clip-corner p-1.5 border border-[rgba(255,90,120,0.35)] text-[#ff9aa8] hover:bg-[rgba(255,90,120,0.1)]"
                      title="Supprimer"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {agLog.length > 0 && (
          <details className="mt-4">
            <summary className="cursor-pointer font-mono-hud text-[10px] tracking-[0.2em] text-[#8b949e] hover:text-white uppercase select-none">
              ▸ Journal d'activité ({agLog.length})
            </summary>
            <ul className="mt-2 space-y-1 text-xs font-mono-hud" data-testid="agnes-log">
              {agLog.slice(0, 10).map((e) => (
                <li key={e.id} className="flex items-center gap-2 text-[#8b949e]">
                  <span className="h-1 w-1 rounded-full bg-[var(--accent)] shrink-0" />
                  <span className="text-white uppercase text-[10px] tracking-[0.15em]">{e.event}</span>
                  <span className="truncate flex-1">{e.detail}</span>
                  <span className="text-[10px] text-[#6b7a8d] shrink-0">
                    {new Date(e.created_at).toLocaleTimeString("fr-FR")}
                  </span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </section>

      {/* D-ID Manager — avatar temps réel, multi-clés rotation */}
      <section className="mt-6 hud-panel reticle clip-corner p-5 sm:p-6" data-testid="did-manager">
        <span className="rc-tr" />
        <span className="rc-bl" />
        <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <Film className="h-4 w-4 accent-text" />
              <h3 className="font-mono-hud text-[10px] tracking-[0.25em] text-[#8b949e] uppercase">
                D-ID · Avatar temps réel · Multi-Clés
              </h3>
            </div>
            <p className="mt-2 text-sm text-[#9fb2c6] max-w-xl leading-relaxed">
              Jusqu'à <strong className="accent-text">{didMax} clés</strong> — quand la clé active tombe en panne de crédits, Sara bascule automatiquement sur la suivante. Zéro coupure de l'avatar.
            </p>
          </div>
          <button
            data-testid="did-add-btn"
            onClick={() => setDidAddOpen(!didAddOpen)}
            disabled={didKeys.length >= didMax}
            className="hud-btn clip-corner px-4 py-2 text-[11px] flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Plus className="h-4 w-4" />
            {didAddOpen ? "ANNULER" : `AJOUTER (${didKeys.length}/${didMax})`}
          </button>
        </div>

        {/* Visage source de Sara */}
        <div className="mb-5 flex items-center gap-4 flex-wrap clip-corner border border-[var(--border-hud)] bg-[rgba(2,8,18,0.4)] p-3.5" data-testid="did-face-selector">
          <div className="relative h-20 w-20 rounded-full overflow-hidden border border-[var(--accent-a25)] shrink-0" style={{ boxShadow: "0 0 18px var(--accent-a25)" }}>
            {didSource?.source_url && (
              <img src={didSource.source_url} alt="Visage de Sara" className="w-full h-full object-cover" data-testid="did-face-preview" />
            )}
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="font-mono-hud text-[10px] tracking-[0.2em] uppercase text-[#8b949e] mb-1">
              Visage source de Sara
            </div>
            <p className="text-[11px] text-[#6b7a8d] mb-2 leading-relaxed">
              C'est la photo qui prend vie en mode « Avatar réel ». Choisis un portrait net, de face, bien éclairé.
            </p>
            <div className="flex gap-2 flex-wrap">
              <button
                data-testid="did-face-upload-btn"
                type="button"
                onClick={() => didFileRef.current?.click()}
                disabled={didUploading}
                className="hud-btn clip-corner px-3 py-2 text-[11px] flex items-center gap-2 disabled:opacity-40"
              >
                {didUploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserRound className="h-3.5 w-3.5" />}
                Changer la photo
              </button>
              {didSource?.is_custom && (
                <button
                  data-testid="did-face-reset-btn"
                  type="button"
                  onClick={resetFace}
                  className="clip-corner px-3 py-2 text-[11px] font-mono-hud tracking-[0.1em] uppercase border border-[var(--border-hud)] text-[#8b949e] hover:text-white hover:bg-[var(--accent-a10)]"
                >
                  Réinitialiser
                </button>
              )}
              <input
                ref={didFileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={uploadFace}
                className="hidden"
                data-testid="did-face-input"
              />
            </div>
          </div>
        </div>

        {didAddOpen && (
          <form onSubmit={addDidKey} className="mt-2 mb-4 grid sm:grid-cols-[1fr_2fr_auto] gap-2.5 items-end" data-testid="did-add-form">
            <div>
              <label className="font-mono-hud text-[10px] tracking-[0.2em] text-[#8b949e] block mb-1.5">NOM</label>
              <input
                data-testid="did-input-name"
                required
                value={didNewName}
                onChange={(e) => setDidNewName(e.target.value)}
                placeholder="Compte D-ID 1"
                className="hud-input"
              />
            </div>
            <div>
              <label className="font-mono-hud text-[10px] tracking-[0.2em] text-[#8b949e] block mb-1.5">CLÉ API D-ID</label>
              <input
                data-testid="did-input-key"
                required
                type="password"
                value={didNewKey}
                onChange={(e) => setDidNewKey(e.target.value)}
                placeholder="xxxxx:yyyyy"
                className="hud-input font-mono-hud"
              />
              <p className="mt-1.5 text-[10px] leading-relaxed text-[#6b7a8d]" data-testid="did-key-hint">
                Copie ta clé depuis <span className="accent-text">D-ID Studio → Account Settings → API Keys</span> (format <span className="accent-text font-mono-hud">identifiant:secret</span>). La clé est validée puis stockée de façon sécurisée.
              </p>
            </div>
            <button
              data-testid="did-submit"
              type="submit"
              disabled={didBusy === "add"}
              className="hud-btn clip-corner px-4 py-2.5 text-[11px] font-bold flex items-center gap-2 whitespace-nowrap"
            >
              {didBusy === "add" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              ENREGISTRER
            </button>
          </form>
        )}

        {didKeys.length === 0 ? (
          <div className="mt-2 clip-corner border border-dashed border-[var(--border-hud)] p-6 text-center text-[#6b7a8d] font-mono-hud text-[10px] tracking-[0.2em] uppercase" data-testid="did-empty">
            Aucune clé D-ID · l'avatar temps réel est inactif tant qu'aucune clé n'est ajoutée.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="did-list">
            {didKeys.map((k) => {
              const revealed = didReveal[k.id];
              const isActive = k.id === didActive;
              const statusColor =
                k.status === "active" ? "#00ffaa" :
                k.status === "quota_exceeded" ? "#f59e0b" :
                k.status === "invalid" ? "#ff5470" : "#8b949e";
              return (
                <li
                  key={k.id}
                  data-testid={`did-row-${k.id}`}
                  className="clip-corner border p-3.5 flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4 bg-[rgba(2,8,18,0.4)]"
                  style={{ borderColor: isActive ? "var(--accent)" : "var(--border-hud)" }}
                >
                  <div className="flex items-start gap-2 min-w-0 w-full sm:flex-1">
                    <span className="h-2 w-2 rounded-full shrink-0 mt-1.5" style={{ background: statusColor, boxShadow: `0 0 8px ${statusColor}` }} />
                    <div className="min-w-0">
                      <div className="text-white font-medium truncate flex items-center gap-2">
                        {k.name}
                        {isActive && (
                          <span className="font-mono-hud text-[9px] tracking-[0.2em] uppercase accent-text border border-[var(--accent-a25)] px-1.5 py-0.5 clip-corner">
                            EN COURS
                          </span>
                        )}
                      </div>
                      <div className="font-mono-hud text-[10px] text-[#8b949e] tracking-[0.1em] flex items-center gap-2">
                        <span className="truncate">{revealed ? k.api_key_masked.replace("...", "•••") : k.api_key_masked}</span>
                        <button
                          onClick={() => setDidReveal({ ...didReveal, [k.id]: !revealed })}
                          className="text-[#6b7a8d] hover:text-white shrink-0"
                          title={revealed ? "Masquer" : "Voir"}
                        >
                          {revealed ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                        </button>
                      </div>
                      <div className="mt-0.5 font-mono-hud text-[10px] uppercase tracking-[0.15em]" style={{ color: statusColor }}>
                        {k.status}
                        {k.credits_left != null && ` · ${k.credits_left.toLocaleString("fr-FR")} crédits restants`}
                      </div>
                      {k.last_error && k.status === "invalid" && (
                        <div className="mt-1 text-[10px] leading-relaxed text-[#f0a6b2]">{k.last_error}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1.5 flex-wrap shrink-0">
                    {!isActive && k.status !== "invalid" && (
                      <button
                        data-testid={`did-activate-${k.id}`}
                        onClick={() => activateDidKey(k.id)}
                        className="clip-corner px-2.5 py-1.5 text-[10px] font-mono-hud tracking-[0.15em] uppercase border border-[var(--accent-a25)] accent-text hover:bg-[var(--accent-a10)]"
                        title="Rendre active"
                      >
                        Activer
                      </button>
                    )}
                    <button
                      data-testid={`did-test-${k.id}`}
                      onClick={() => testDidKey(k.id)}
                      disabled={didBusy === k.id}
                      className="clip-corner p-1.5 border border-[var(--border-hud)] text-[#8b949e] hover:text-white hover:bg-[var(--accent-a10)] disabled:opacity-40"
                      title="Vérifier la clé et les crédits"
                    >
                      {didBusy === k.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                    </button>
                    <button
                      data-testid={`did-delete-${k.id}`}
                      onClick={() => deleteDidKey(k.id)}
                      className="clip-corner p-1.5 border border-[rgba(255,90,120,0.35)] text-[#ff9aa8] hover:bg-[rgba(255,90,120,0.1)]"
                      title="Supprimer"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {didLog.length > 0 && (
          <details className="mt-4">
            <summary className="cursor-pointer font-mono-hud text-[10px] tracking-[0.2em] text-[#8b949e] hover:text-white uppercase select-none">
              ▸ Journal d'activité ({didLog.length})
            </summary>
            <ul className="mt-2 space-y-1 text-xs font-mono-hud" data-testid="did-log">
              {didLog.slice(0, 10).map((e) => (
                <li key={e.id} className="flex items-center gap-2 text-[#8b949e]">
                  <span className="h-1 w-1 rounded-full bg-[var(--accent)] shrink-0" />
                  <span className="text-white uppercase text-[10px] tracking-[0.15em]">{e.event}</span>
                  <span className="truncate flex-1">{e.detail}</span>
                  <span className="text-[10px] text-[#6b7a8d] shrink-0">
                    {new Date(e.created_at).toLocaleTimeString("fr-FR")}
                  </span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </section>

      {/* Profil Auto-Fill */}
      <section className="mt-6 hud-panel reticle clip-corner p-5 sm:p-6" data-testid="profile-fields-section">
        <span className="rc-tr" />
        <span className="rc-bl" />
        <div className="flex items-center gap-2 mb-4">
          <UserRound className="h-4 w-4 accent-text" />
          <h3 className="font-mono-hud text-[10px] tracking-[0.25em] text-[#8b949e] uppercase">
            Profil pour auto-remplissage
          </h3>
        </div>
        <p className="text-sm text-[#9fb2c6] mb-4 max-w-2xl">
          Ces infos sont utilisées par Sara + l'extension pour remplir automatiquement
          les formulaires (adresse de livraison, coordonnées, réservations).
          Elles ne quittent jamais ton compte et ne sont partagées avec aucun site tiers.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          {[
            ["full_name", "Nom complet", "Marc Olivier Dupont"],
            ["phone", "Téléphone", "+33 6 12 34 56 78"],
            ["address_line1", "Adresse", "12 rue de la Paix"],
            ["address_line2", "Complément (opt.)", "Bâtiment B, appt 3"],
            ["zip_code", "Code postal", "75002"],
            ["city", "Ville", "Paris"],
            ["country", "Pays", "France"],
          ].map(([k, label, ph]) => (
            <div key={k}>
              <label className="font-mono-hud text-[10px] tracking-[0.2em] text-[#8b949e] block mb-1.5">
                {label.toUpperCase()}
              </label>
              <input
                data-testid={`profile-${k}`}
                value={profile[k] || ""}
                onChange={(e) => setProfile({ ...profile, [k]: e.target.value })}
                placeholder={ph}
                className="hud-input"
              />
            </div>
          ))}
        </div>
        <button
          data-testid="profile-save"
          onClick={saveProfile2}
          disabled={savingProf}
          className="hud-btn clip-corner px-5 py-2.5 text-[11px] mt-4 flex items-center gap-2 font-bold"
        >
          {savingProf ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
          ENREGISTRER LE PROFIL
        </button>
      </section>

      {/* Sara Copilote — extension navigateur */}
      <section className="mt-6 hud-panel reticle clip-corner p-5 sm:p-6" data-testid="agent-section">
        <span className="rc-tr" />
        <span className="rc-bl" />
        <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <Puzzle className="h-4 w-4 accent-text" />
              <h3 className="font-mono-hud text-[10px] tracking-[0.25em] text-[#8b949e] uppercase">
                Sara Copilote — extension Chrome
              </h3>
            </div>
            <p className="mt-2 text-sm text-[#9fb2c6] max-w-xl leading-relaxed">
              Installe l'extension pour laisser Sara agir <strong className="accent-text">directement dans ton navigateur</strong> :
              ouvrir des onglets, cliquer, remplir des formulaires, ajouter au panier, lire une page.
            </p>
          </div>
          <div
            data-testid="agent-status-badge"
            className="flex items-center gap-2 clip-corner px-3 py-1.5 border"
            style={{
              borderColor: agentStatus?.online ? "#00ffaa" : agentStatus?.paired ? "#f59e0b" : "rgba(139,148,158,0.3)",
              background: agentStatus?.online ? "rgba(0,255,170,0.08)" : "rgba(2,8,18,0.5)",
            }}
          >
            <Radio
              className="h-3.5 w-3.5"
              style={{ color: agentStatus?.online ? "#00ffaa" : agentStatus?.paired ? "#f59e0b" : "#8b949e" }}
            />
            <span className="font-mono-hud text-[10px] tracking-[0.15em] uppercase">
              {agentStatus?.online ? "En ligne" : agentStatus?.paired ? "Installée · hors ligne" : "Non installée"}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <a
            data-testid="agent-download-zip"
            href={`${process.env.PUBLIC_URL || ""}/sara-copilote.zip`}
            download="sara-copilote.zip"
            className="hud-btn clip-corner px-4 py-2.5 text-[11px] flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            TÉLÉCHARGER L'EXTENSION (.zip)
          </a>
          <a
            data-testid="agent-open-extensions"
            href="chrome://extensions/"
            className="clip-corner px-4 py-2.5 text-[11px] flex items-center gap-2 border border-[var(--border-hud)] text-[#8b949e] hover:text-white hover:bg-[var(--accent-a10)] font-mono-hud tracking-[0.15em] uppercase"
            onClick={(e) => {
              e.preventDefault();
              toast.info("Ouvre manuellement chrome://extensions/ — Chrome bloque les liens directs vers cette page.");
              navigator.clipboard?.writeText("chrome://extensions/").catch(() => {});
            }}
          >
            <Puzzle className="h-4 w-4" /> Ouvrir chrome://extensions
          </a>
          {agentStatus?.paired && (
            <button
              data-testid="agent-revoke"
              onClick={revokeAgent}
              disabled={revoking}
              className="clip-corner px-4 py-2.5 text-[11px] flex items-center gap-2 border border-[rgba(255,90,120,0.4)] text-[#ff9aa8] hover:bg-[rgba(255,90,120,0.1)] font-mono-hud tracking-[0.15em] uppercase disabled:opacity-50"
            >
              <PowerOff className="h-4 w-4" />
              {revoking ? "..." : "Déconnecter"}
            </button>
          )}
        </div>

        <details className="mt-5 group" data-testid="agent-instructions">
          <summary className="cursor-pointer font-mono-hud text-[10px] tracking-[0.2em] text-[#8b949e] hover:text-white uppercase select-none">
            ▸ Instructions d'installation (30 s)
          </summary>
          <ol className="mt-3 space-y-1.5 text-sm text-[#9fb2c6] list-decimal pl-6">
            <li>Télécharge le .zip ci-dessus et dézippe-le.</li>
            <li>Va sur <code className="text-white bg-[rgba(2,8,18,0.9)] px-1.5 py-0.5 rounded text-xs">chrome://extensions/</code>.</li>
            <li>Active <strong>Mode développeur</strong> (coin haut-droit).</li>
            <li>Clique <strong>Charger l'extension non empaquetée</strong> et choisis le dossier dézippé.</li>
            <li>Épingle l'icône Sara, ouvre la popup et connecte-toi avec ton email/mot de passe.</li>
          </ol>
        </details>

        {agentStatus?.recent?.length > 0 && (
          <div className="mt-5">
            <div className="font-mono-hud text-[10px] tracking-[0.2em] text-[#8b949e] uppercase mb-2">
              Dernières actions
            </div>
            <ul className="space-y-1 text-xs" data-testid="agent-recent">
              {agentStatus.recent.slice(0, 5).map((c) => (
                <li key={c.id} className="flex items-center gap-2 font-mono-hud">
                  <span
                    className="h-1.5 w-1.5 rounded-full shrink-0"
                    style={{
                      background:
                        c.status === "done" ? "#00ffaa" :
                        c.status === "error" ? "#ff5470" :
                        c.status === "in_progress" ? "#f59e0b" : "#4a5568",
                    }}
                  />
                  <span className="text-white">{c.action}</span>
                  <span className="text-[#6b7a8d] truncate">
                    {c.error ? `— ${c.error}` : c.params?.url || c.params?.query || c.params?.selector || ""}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* Sara Connor Companion — application Android */}
      <section className="mt-6 hud-panel reticle clip-corner p-5 sm:p-6" data-testid="companion-android-section">
        <span className="rc-tr" />
        <span className="rc-bl" />
        <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <Smartphone className="h-4 w-4 accent-text" />
              <h3 className="font-mono-hud text-[10px] tracking-[0.25em] text-[#8b949e] uppercase">
                Sara Connor Companion — App Android
              </h3>
            </div>
            <p className="mt-2 text-sm text-[#9fb2c6] max-w-xl leading-relaxed">
              L'app Android compagnon : commande <strong className="accent-text">à la voix</strong> les actions
              du téléphone (SMS, appel, e-mail, Maps, apps, shopping) avec confirmation, et un module
              <strong className="accent-text"> Vision IA</strong> (caméra avant/arrière). Se connecte à ton compte Sara.
            </p>
          </div>
          <div
            className="flex items-center gap-2 clip-corner px-3 py-1.5 border"
            style={{ borderColor: "rgba(34,211,238,0.4)", background: "rgba(34,211,238,0.06)" }}
          >
            <Smartphone className="h-3.5 w-3.5 accent-text" />
            <span className="font-mono-hud text-[10px] tracking-[0.15em] uppercase accent-text">Projet Android Studio</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <a
            data-testid="companion-download-zip"
            href={`${process.env.PUBLIC_URL || ""}/sara-connor-companion-android.zip`}
            download="sara-connor-companion-android.zip"
            className="hud-btn clip-corner px-4 py-2.5 text-[11px] flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            TÉLÉCHARGER LE PROJET ANDROID (.zip)
          </a>
        </div>

        <details className="mt-5 group" data-testid="companion-instructions">
          <summary className="cursor-pointer font-mono-hud text-[10px] tracking-[0.2em] text-[#8b949e] hover:text-white uppercase select-none">
            ▸ Compiler l'APK (Android Studio)
          </summary>
          <ol className="mt-3 space-y-1.5 text-sm text-[#9fb2c6] list-decimal pl-6">
            <li>Télécharge le .zip et dézippe-le.</li>
            <li>Ouvre <strong>Android Studio</strong> → <em>File &gt; Open</em> → dossier <code className="text-white bg-[rgba(2,8,18,0.9)] px-1.5 py-0.5 rounded text-xs">SaraConnorCompanion</code>.</li>
            <li>Laisse Gradle synchroniser (JDK 17, compileSdk 34 — installés automatiquement).</li>
            <li>Branche ton téléphone (débogage USB) puis clique <strong>Run ▶</strong>, ou <em>Build &gt; Build APK(s)</em>.</li>
            <li>APK debug : <code className="text-white bg-[rgba(2,8,18,0.9)] px-1.5 py-0.5 rounded text-xs">app/build/outputs/apk/debug/app-debug.apk</code>.</li>
            <li>Ouvre l'app, connecte-toi avec ce même compte, autorise micro/caméra à la demande.</li>
          </ol>
          <p className="mt-3 font-mono-hud text-[10px] text-[#6b7a8d] leading-snug">
            Détails complets dans BUILD_INSTRUCTIONS.md (inclus dans le .zip). Le site n'est pas modifié :
            l'app utilise l'API companion en lecture/exécution.
          </p>
        </details>
      </section>

    </AppShell>
  );
}
